import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { isAxiosError } from 'axios';
import {
  listDeploySources,
  createDeploySource,
  updateDeploySource,
  rotateDeploySourceSecret,
  deleteDeploySource,
} from '../api/deploy-sources.api.ts';
import type { DeploySourceListParams } from '@/shared/types/deploy.ts';
import type { CreateDeploySourceDTO, UpdateDeploySourceDTO } from '../api/deploy-sources.api.ts';

export const deploySourceKeys = {
  all: ['deploy-sources'] as const,
  list: (params: DeploySourceListParams) => [...deploySourceKeys.all, 'list', params] as const,
  detail: (id: string) => [...deploySourceKeys.all, 'detail', id] as const,
};

export function useDeploySourceList(params: DeploySourceListParams, enabled = true) {
  return useQuery({
    queryKey: deploySourceKeys.list(params),
    queryFn: () => listDeploySources(params),
    enabled,
  });
}

export function useCreateDeploySource(onSuccess: (secret: string, id: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDeploySourceDTO) => createDeploySource(input),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: deploySourceKeys.all });
      toast.success('Repositório cadastrado');
      onSuccess(data.webhookSecret, data.id);
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error(err.response.data?.error ?? 'Repositório já cadastrado');
        return;
      }
      toast.error('Erro ao cadastrar repositório');
    },
  });
}

export function useUpdateDeploySource(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDeploySourceDTO }) =>
      updateDeploySource(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: deploySourceKeys.all });
      toast.success('Repositório atualizado');
      onSuccess();
    },
    onError: () => {
      toast.error('Erro ao atualizar repositório');
    },
  });
}

export function useRotateDeploySourceSecret(onSuccess: (secret: string, id: string) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rotateDeploySourceSecret(id),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: deploySourceKeys.all });
      toast.success('Novo segredo gerado — atualize o webhook no Bitbucket');
      onSuccess(data.webhookSecret, data.id);
    },
    onError: () => {
      toast.error('Erro ao gerar novo segredo');
    },
  });
}

export function useDeleteDeploySource(onSuccess: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteDeploySource(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: deploySourceKeys.all });
      toast.success('Repositório removido');
      onSuccess();
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 422) {
        toast.error(err.response.data?.error ?? 'Há deploys pendentes nesta origem');
        return;
      }
      toast.error('Erro ao remover repositório');
    },
  });
}
